/**
 * Capa de conexión con el servidor (Socket.IO).
 *
 * Un único socket por pestaña + envoltorios promise para todos los eventos
 * que responden por ACK. Los eventos de broadcast (room:users, queue:state…)
 * NO viven aquí: los consume el provider (hooks/useJamRoom) vía onServerEvent.
 */
import { io } from 'socket.io-client';

const SERVER_URL =
  import.meta.env?.VITE_SERVER_URL ||
  (import.meta.env.PROD ? window.location.origin : 'http://localhost:4000');
const ACK_TIMEOUT_MS = 30_000;

/** Error con el código público que emite el backend (duplicate_track…). */
export class JamApiError extends Error {
  constructor(code, message) {
    super(message || code);
    this.name = 'JamApiError';
    this.code = code || 'error';
  }
}

let socket = null;

function ensureSocket() {
  if (socket) return socket;
  socket = io(SERVER_URL, {
    transports: ['websocket'],
    // socket.io hace reconnection automática; limitamos backoff a 5 s.
    reconnectionDelayMax: 5_000,
  });
  return socket;
}

/** Conecta (o devuelve) el socket singleton. */
export function connectSocket() {
  return ensureSocket();
}

/** URL raíz del backend (también usada por el relay de audio). */
export { SERVER_URL };

/** Suscripción a un evento broadcast del servidor. Devuelve la función de desuscripción. */
export function onServerEvent(event, handler) {
  const s = ensureSocket();
  s.on(event, handler);
  return () => s.off(event, handler);
}

function emitAck(event, payload) {
  return new Promise((resolve, reject) => {
    const s = ensureSocket();
    const timer = setTimeout(
      () => reject(new JamApiError('ack_timeout', `${event} no respondió a tiempo`)),
      ACK_TIMEOUT_MS,
    );
    // socket.io almacena en buffer los emit previos a la conexión.
    s.emit(event, payload, (res) => {
      clearTimeout(timer);
      if (res && res.ok === false) {
        reject(new JamApiError(res.error?.code ?? 'error', res.error?.message));
      } else {
        resolve(res ?? {});
      }
    });
  });
}

/** API de la sala en formato promesa. */
export const jamApi = {
  joinRoom: (p) => emitAck('room:join', p),
  leaveRoom: (p = {}) => emitAck('room:leave', p),
  searchTrack: (p) => emitAck('search_track', p),
  addToQueue: (p) => emitAck('add_to_queue', p),
  removeFromQueue: (p) => emitAck('remove_from_queue', p),
  reorderQueue: (p) => emitAck('queue:reorder', p),
  getStreamUrl: (p) => emitAck('get_stream_url', p),
  playbackControl: (p) => emitAck('playback:control', p),
  syncPlayback: (p) => emitAck('sync_playback', p),
  syncRequest: (p) => emitAck('sync:request', p),
  /** Reporta fallo de stream; devuelve URL fresca (y dispara stream:refresh a la sala). */
  reportStreamError: (p) => emitAck('stream:error', p),
};
